import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { Footer } from './Footer'
import { ImpersonationBanner } from '@/components/impersonation/ImpersonationBanner'
import { AffiliateCompanyBankBanner } from '@/components/banners/AffiliateCompanyBankBanner'
import { HotelProfileContactsBanner } from '@/components/banners/HotelProfileContactsBanner'
import { PlatformNoticeBanner } from '@/components/platform-notice/PlatformNoticeBanner'
import { PlatformNoticeModal } from '@/components/platform-notice/PlatformNoticeModal'
import { AffiliationRequestNotification } from '@/components/notifications/AffiliationRequestNotification'
import { SkipLinks } from '@/components/accessibility/SkipLinks'
import clsx from 'clsx'

interface LayoutProps {
  children?: React.ReactNode
}

const SIDEBAR_COLLAPSED_KEY = 'hoteliate:sidebar-collapsed'

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
    return saved === 'true'
  })

  // Persist sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed))
  }, [sidebarCollapsed])

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => !prev)
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <SkipLinks />

      <nav role="navigation" aria-label="Main navigation">
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebarCollapse}
        />
      </nav>

      {/* Main content area.
          The desktop sidebar is `position: fixed` at `lg:start-0`,
          which is already a logical property — it docks to LEFT in
          LTR and RIGHT in RTL. The padding must therefore live on
          the SAME logical side, so `ps-` (padding-inline-start) is
          correct in both directions. The previous code branched
          `isRTL ? "pe-72" : "ps-72"` which double-flipped: in RTL,
          `pe-` resolves to LEFT padding, while the sidebar is on
          the right — leaving `<main>` to extend under the sidebar
          and clip the rightmost KPI card on every Hebrew desktop
          view. Caught by the live UI review (live review 2026-05-08
          finding #1). */}
      <div className={clsx(
        "flex flex-col flex-1 transition-all duration-300",
        sidebarCollapsed ? "lg:ps-20" : "lg:ps-72"
      )}>
        {/* Header component renders its own <header> element (which
            implicitly has role=banner). The earlier wrapping <header
            role="banner"> here was redundant AND broke the inner
            Header's position:sticky — wrapping a sticky element in
            a parent whose height equals the sticky's own height
            leaves no room for the stick. The inner Header would
            scroll off normally and the auto-hide transform from
            commit 0e9d5d9c had no effect (user reported: 'on all
            pages when scrolling back up the top bar with the logo
            should reappear'). Wrapper removed; Header pins to the
            viewport now. */}
        {/* Banners live INSIDE the padded content column: rendered outside it
            they spanned the full viewport width, and the first ~288px of text
            sat underneath the fixed sidebar (clipped on every desktop view). */}
        {/* #2072 — platform-wide notice for every user type (self-gated). The
            entry modal portals to <body> above the sticky header. */}
        <PlatformNoticeBanner />
        <PlatformNoticeModal />
        <ImpersonationBanner />
        {/* #629 — affiliates with incomplete Company & Bank Details (self-gated) */}
        <AffiliateCompanyBankBanner />
        {/* #1443 — hotels missing an Affiliation or Finance Contact (self-gated) */}
        <HotelProfileContactsBanner />

        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main role="main" id="main-content" className="flex-1 flex flex-col min-h-0 py-2 sm:py-6">
          <div className="w-full flex-1 min-h-0 flex flex-col px-2 sm:px-6 lg:px-8">
            {children || <Outlet />}
          </div>
        </main>

        <footer role="contentinfo">
          <Footer />
        </footer>
      </div>

      {/* Floating notification for new affiliation requests */}
      <AffiliationRequestNotification />
    </div>
  )
}